import { IOrderApiFilterParams } from '../../../interfaces/orders/order-interfaces';

/**
 * Order filter model
 *
 * @version
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.48.0 OAUI-582 Add Date Range Filter for Orders
 * 4.37.0 OAUI-532 Add missing filters for v2 orders list
 * 4.37.0 OAUI-476 Add filter for order types
 * 4.32.0 OAUI-470 Filtering is using incorrect fulfillment group filter
 * 4.31.0 OAUI-455 Add basic table to display orders
 * 4.31.0 OAUI-453 Set up basic list settings, filters and layout
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
export class OrderFilterModel {
  private readonly dateStart?: Date;
  private readonly dateEnd?: Date;
  private readonly dateType?: any;
  private readonly lastId?: string | null;
  private readonly limit?: number | null;
  private readonly jobFilter?: any;
  private readonly orderNumberFilter?: string;
  private readonly orderType?: any;
  private readonly orderShippingTypes?: Array<any>;
  private readonly orderStatuses?: Array<any>;
  private readonly orderedForFilter?: any;
  private readonly organizationFilter?: any;
  private readonly productionStatuses?: Array<any>;
  private readonly deliveryStatuses?: Array<any>;
  private readonly fulfillmentGroups?: Array<any>;
  private readonly seatTypes?: Array<any>;
  private readonly userGroups?: Array<any>;
  private readonly includedLookups?: string;

  /**
   * Creates an instance of OrderFilterModel.
   *
   * @since 4.31.0
   * @memberof OrderFilterModel
   */
  constructor(
    dateStart: Date,
    dateEnd: Date,
    dateType: string,
    lastId: string,
    limit: number,
    jobFilter: any,
    orderNumberFilter: string,
    orderType: any,
    orderShippingTypes: Array<any>,
    orderStatuses: Array<any>,
    orderedForFilter: any,
    organizationFilter: any,
    productionStatuses: Array<any>,
    deliveryStatuses: Array<any>,
    fulfillmentGroups: Array<any>,
    seatTypes: Array<any>,
    userGroups: Array<any>,
    includedLookups: string,
  ) {
    this.dateStart = dateStart;
    this.dateEnd = dateEnd;
    this.dateType = dateType;
    this.lastId = lastId;
    this.limit = limit;
    this.jobFilter = jobFilter;
    this.orderNumberFilter = orderNumberFilter;
    this.orderType = orderType;
    this.orderShippingTypes = orderShippingTypes;
    this.orderStatuses = orderStatuses;
    this.orderedForFilter = orderedForFilter;
    this.organizationFilter = organizationFilter;
    this.productionStatuses = productionStatuses;
    this.deliveryStatuses = deliveryStatuses;
    this.fulfillmentGroups = fulfillmentGroups;
    this.seatTypes = seatTypes;
    this.userGroups = userGroups;
    this.includedLookups = includedLookups;
  }

  /**
   * Append filters to a results array (If applicable)
   *
   * @since 4.31.0
   * @param result - Result Object
   * @param arr - Target Array
   * @param objKey - Object Key
   * @param outKey - Result Key
   * @param storedValues - Filter values of type saved to local storage
   * @param storedTotal -Total filters of type saved to local storage
   * @memberof OrderFilterModel
   */
  appendFilters(
    result: any,
    arr: Array<any> | undefined,
    objKey: string,
    outKey: string,
    storedValues: Array<any>,
    storedTotal: number,
  ): void {
    // If the filters data exists, add the selected values as parameters
    if (arr) {
      // Get selected values from filters data
      const mappedKeys = arr.reduce(
        (arr, obj) => (obj.selected ? arr.concat(obj[objKey]) : arr),
        [],
      );
      // Avoid sending filters where everything is selected
      if (mappedKeys.length !== arr.length) {
        result[outKey] = mappedKeys.join(',');
      }
    } else if (storedValues) {
      // If filters data is not yet available, use last known saved filters
      // Avoid sending filters where everything is selected
      if (storedValues.length !== storedTotal) {
        result[outKey] = storedValues.join(',');
      }
    }
  }

  /**
   * Get the serialized object instance for query params
   *
   * @since 4.31.0
   * @memberof OrderFilterModel
   */
  getSerialized(orderSettings: any): IOrderApiFilterParams {
    // Core Filters
    const result: any = {
      jobId: this.jobFilter ? this.jobFilter.index : null,
      lastId: this.lastId ? this.lastId : null,
      limit: this.limit ? this.limit : null,
      orderType: this.orderType ? this.orderType : null,
      orderedForUserId: this.orderedForFilter ? this.orderedForFilter.id : null,
      organizationId: this.organizationFilter
        ? this.organizationFilter.index
        : null,
    };
    // Date Range Filters
    if (this.dateType && (this.dateStart || this.dateEnd)) {
      result.dateStart = this.dateStart;
      result.dateEnd = this.dateEnd;
      result.dateType = this.dateType.value;
    }

    if (this.orderNumberFilter) {
      result.orderNumber = this.orderNumberFilter;
      result.matchType = 'pattern';
    }

    // Specify the includeLookups parameter as a comma-separated list of
    // data to be included in the order via $lookup in the order API.
    // Note: Currently, only 'vehicleAssignments' is supported.
    result.includeLookups = this.includedLookups;

    // Selected Order Settings
    const selectedSettings = orderSettings.selected;

    // Append order shipping type filters to result
    this.appendFilters(
      result,
      this.orderShippingTypes,
      'value',
      'shippingType',
      selectedSettings.types.shipping.values,
      selectedSettings.types.shipping.total,
    );
    // Append order statuses filters to result
    this.appendFilters(
      result,
      this.orderStatuses,
      'id',
      'status',
      selectedSettings.statuses.order.ids,
      selectedSettings.statuses.order.total,
    );
    // Append shipping statuses filters to result
    this.appendFilters(
      result,
      this.deliveryStatuses,
      'index',
      'deliveryStatusId',
      selectedSettings.statuses.shipping.indexes,
      selectedSettings.statuses.shipping.total,
    );
    // Append production statuses filters to result
    this.appendFilters(
      result,
      this.productionStatuses,
      'index',
      'productionStatusId',
      selectedSettings.statuses.production.indexes,
      selectedSettings.statuses.production.total,
    );
    // Append fulfillment groups filters to result
    this.appendFilters(
      result,
      this.fulfillmentGroups,
      'index',
      'fulfillmentGroupId',
      selectedSettings.groups.fulfillment.indexes,
      selectedSettings.groups.fulfillment.total,
    );
    // Append seat types filters to result
    this.appendFilters(
      result,
      this.seatTypes,
      'bcxId',
      'seatType',
      selectedSettings.types.seat.values,
      selectedSettings.types.seat.total,
    );

    // Append user groups filters to result
    this.appendFilters(
      result,
      this.userGroups,
      'id',
      'userGroupId',
      selectedSettings.groups.user.ids,
      selectedSettings.groups.user.total,
    );

    return result;
  }
}
